/**
 * Sesotho: Billing
 *
 * What this file covers: Credits, plans, top-ups, the ledger, usage, alerts and invoices.
 *
 * Tone: Exact about money. Credits are what is spent; R is used only for rand actually paid. Never use words
 * like margin, markup or profit. Write amounts and dates the way the language writes them.
 *
 * Sesotho: Everyday Sesotho as used in South African business. Keys not translated yet fall back to English, so
 * a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const billing: Catalog = {
  'billing.currency': 'Chelete',
  'billing.fullLedger': 'Buka e felletseng →',
  'billing.ledger.columns.when': 'Neng',
  'billing.ledger.loadError': 'Ha ea khona ho kenya buka ea litšenyehelo.',
  'billing.ledger.title': 'Buka ea litšenyehelo',
  'billing.lowBalance': 'Chelete e tlase',
  'billing.emptyBalance': 'Ha ho chelete',
  'billing.noTransactionsYet': 'Ha ho litšebelisano hajoale.',
  'billing.recentTransactions': 'Litšebelisano tsa morao tjena',
  'billing.topUp': 'Tlatsa →',
  'billing.topUpNow': 'Tlatsa hona joale →',
  'billing.topup.pay': 'Lefa',
  'billing.topup.paymentMethod': 'Mokhoa oa ho lefa',
  'billing.topup.topUp': 'Ho tlatsa',
  'billing.walletBalance': 'Chelete e sepacheng',
  'billing.alerts.low.emailPlaceholder': 'finance@example.com, owner@example.com',
  'billing.alerts.low.msisdnPlaceholder': '+27 82 000 1234',
  'billing.category.meta': 'Messenger le Instagram',
  'billing.ledger.refundedTotal': 'Ho khutliselitsoe',
  'billing.ledger.planFeesTotal': 'Litefiso tsa moralo',
  'billing.ledger.spentHint': 'Melaetsa, AI le polokelo',
  'billing.ledger.planFeesHint': 'Ngoliso ea hao, ho tlosoa mokoloto ofe kapa ofe oa phetoho ea moralo',
  'billing.ledger.addedHint': 'Ho kenya chelete, limpho le mekoloto e kenyellelitsoeng morerong oa hao',
  'billing.ledger.totalsNote': 'Tse eketsitsoeng le tse khutliselitsoeng, ho tlosoa tse sebelisitsoeng le litefiso tsa moralo, ke chelete ea hao ha ho se na sefe.',
  'billing.ledger.exportFailed': 'Buka ea lichelete ha ea ka ea romeloa kantle. Leka hape.',
  'billing.ledger.exporting': 'E romela kantle…',
  'billing.plan.limits.title': 'Se se dumellang',
  'billing.plan.limit.bots': 'Di-chatbot',
  'billing.plan.limit.numbers': 'Dinomoro tsa WhatsApp',
  'billing.plan.limit.team_members': 'Ditho tsa sehlopha',
  'billing.plan.limit.flows': 'Phallo ya chatbot',
  'billing.plan.limit.knowledge_documents': 'Ditokomane tsa setsi sa tsebo',
  'billing.plan.limit.unlimited': 'Ha ho moedi',
  'billing.wallet.bought': 'Tse rekilweng: {{amount}}',
  'billing.wallet.fromPlan': 'Tse tswang moralong wa hao: {{amount}}',
  'billing.wallet.split.info': 'Dikrediti tse tswang moralong wa hao di sebediswa pele mme ke tsa melaetsa. Tefo ya kgwedi ya moralo wa hao e lefuwa ka dikrediti tseo o di rekileng.',
  'billing.wallet.split.infoLabel': 'Mabapi le dikrediti tse rekilweng le tse tswang moralong wa hao',
  'billing.plan.prices.title': 'Ditheko tsa melaetsa, ka dikrediti',
  'billing.plan.prices.marketing': 'Molaetsa wa papatso',
  'billing.plan.prices.utility': 'Molaetsa wa tsebiso',
  'billing.plan.prices.service': 'Karabo nakong ya dihora tse 24',
  'billing.plan.prices.cheaper': 'Ditheko tsa melaetsa tse tlase ho tseo o di lefang hona jwale',
  'billing.plan.refused.title': 'Dikrediti tse rekilweng ha di a lekana bakeng sa {{name}}',
  'billing.plan.refused.body': 'Tefo ya moralo e lefuwa ka dikrediti tseo o di rekileng. Dikrediti tse rekilweng tse setseng: {{bought}}. Tse hlokahalang: {{required}}. Tse tswang moralong wa hao ({{included}}) di ka sebediswa melaetseng feela.',
  'billing.plan.refused.next': 'Tlatsa {{shortfall}} kapa ho feta, ebe o kgetha {{name}} hape.',
  'billing.usage.rates.view': 'Ditheko tse tla bontshwa',
  'billing.usage.rates.view.mine': 'Ditheko tsa hao',
  'billing.usage.rates.view.compare': 'Bapisa meralo',
  'billing.usage.rates.changePlan': 'Fetola moralo →',
  'billing.usage.rates.compareHint': 'Seo yuniti e le nngwe e se bitsang moralong ka mong, ka dikrediti. Moralo wa hao o tshwailwe.',
  'billing.usage.rates.yourPlan': 'Moralo wa hao',
  'billing.usage.rates.yourPlanPrice': 'Theko ya hao ya {{plan}}',
  'billing.usage.rates.payg': 'Theko ya ho lefa ha o ntse o sebedisa:',
  'billing.usage.rates.planNote': 'O moralong wa {{plan}}. Moo theko ya hao e leng tlase ho theko ya ho lefa ha o ntse o sebedisa, theko eo e bontshwa e sehilwe ka mola pela ya hao.',
  'billing.explain.planPrice': 'E lefisitswe ka theko ya hao ya {{plan}} (ho lefa ha o ntse o sebedisa ke {{list}})',
  'billing.explain.planPriceUnnamed': 'E lefisitswe ka theko ya moralo wa hao (ho lefa ha o ntse o sebedisa ke {{list}})',
  'billing.plan.modalBody': 'Tefo ya kgwedi e lefuwa ka dikrediti tseo o di rekileng. Dikrediti tse kenyeleditsweng moralong di eketswa sepatjheng sa hao nako le nako ha ho lefuwa, mme di sebediswa pele, melaetseng. Ha moralo o le hodimo, molaetsa ka mong o bitsa hanyane.',
  'billing.topup.providerDisabled': 'Mokhoa oo oa ho lefa o sa tsoa koaloa. Khetha o mong, kapa u leke hape hamorao.',
  'billing.alerts.methods.unavailable.title': 'Ho boloka karete ha ho fumanehe hajoale',
  'billing.alerts.methods.unavailable.body': 'Likarete tseo u seng u li bolokile li lula mona, empa ho tlatsa ka boiketsetso ho emisitsoe ho fihlela ho lefa ka karete ho buloa hape. U ntse u ka tlatsa ka letsoho.',
  'billing.topup.result.checking.title': 'Re netefatsa tefo ea hao',
  'billing.topup.result.checking.body': 'Re emetse hore {{provider}} e e netefatse. Hangata sena se nka metsotsoana e seng mekae.',
  'billing.topup.result.checking.bodyPlain': 'Hangata sena se nka metsotsoana e seng mekae.',
  'billing.topup.result.slow.title': 'Re ntse re netefatsa',
  'billing.topup.result.slow.body': '{{provider}} ha e so netefatse tefo ea hao. Likhredithi tsa hao li tla hlaha hang ha e e netefalitse, kahoo u ka koala sena.',
  'billing.topup.result.success.title': 'Tefo e atlehile',
  'billing.topup.result.success.body': 'Ho kentsoe {{credits}} sepacheng sa hao.',
  'billing.topup.result.success.receipt': 'Rasiti e tseleng e ea imeileng ea hao.',
  'billing.topup.result.failed.title': 'Tefo ha ea phethoa',
  'billing.topup.result.failed.body': '{{provider}} ha ea phethela tefo ena, kahoo ha ho likhredithi tse kentsoeng. Haeba chelete e tsoile akhaontong ea hao, ikopanye le rona u sebelisa tshupiso e ka tlase.',
  'billing.topup.result.paid': 'E lefiloe',
  'billing.topup.result.added': 'Likhredithi tse kentsoeng',
  'billing.topup.result.balance': 'Chelete e sepacheng joale',
  'billing.topup.result.reference': 'Tshupiso {{reference}}',
  'billing.topup.result.tryAgain': 'Leka hape',
  'billing.topup.result.done': 'Ho lokile'
};

export default billing;
